import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  ChevronLeft,
  Bookmark,
  Clock,
  Headphones,
  Play,
  Pause,
  Heart,
  Share2,
  MessageCircle,
} from "lucide-react";
import Loader from "../components/loader";

export default function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [sharesCount, setSharesCount] = useState(0);
  const [savesCount, setSavesCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);

  const userId = useMemo(() => localStorage.getItem("userId"), []);
  const userName = useMemo(() => localStorage.getItem("userName") || "Anonymous", []);

  useEffect(() => {
    const loadBlog = async () => {
      const res = await api.get(`/api/blogs/${id}`);
      setBlog(res.data);

      // Log activity
      try {
        await api.post("/api/activities/add-activity", {
          message: "Read a health education article",
        });
      } catch (err) {
        console.error("Failed to log blog read activity", err);
      }

      // ✅ Save to localStorage when opened
      let read = JSON.parse(localStorage.getItem("readBlogs")) || [];
      // avoid duplicates
      if (!read.find((b) => b._id === res.data._id)) {
        read.unshift(res.data); // add to front
        if (read.length > 10) read = read.slice(0, 10); // keep max 10 recent
        localStorage.setItem("readBlogs", JSON.stringify(read));
      }
    };
    loadBlog();
  }, [id]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/blogs/${id}`);
        if (!mounted) return;
        const data = res.data;
        setBlog(data);

        const currentUserId = localStorage.getItem("userId");
        setLiked((data.likes || []).includes(currentUserId));
        setSaved((data.saves || []).includes(currentUserId));
        setLikesCount((data.likes || []).length);
        setSharesCount(data.shares || 0);
        setSavesCount((data.saves || []).length);

        let relatedRes;
        try {
          relatedRes = await api.get(`/api/blogs/related/${id}`);
        } catch (e) {
          console.warn("No related endpoint, fallback to all blogs");
        }

        if (!relatedRes?.data?.length) {
          const allRes = await api.get("/api/blogs");
          const filtered = allRes.data.blogs
            .filter((b) => b._id !== id)
            .slice(0, 5);
          setRelatedBlogs(filtered);
        } else {
          setRelatedBlogs(relatedRes.data);
        }

        const commentsRes = await api.get(`/api/blogs/${id}/comments`);
        if (!mounted) return;
        setComments(commentsRes.data.comments || []);
      } catch (err) {
        console.error("Failed to load blog", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
      window.speechSynthesis.cancel();
    };
  }, [id]);

  const toggleSpeech = () => {
    const synth = window.speechSynthesis;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
    } else if (blog?.content) {
      const utterance = new SpeechSynthesisUtterance(blog.content);
      utterance.lang = "en-US";
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onend = () => setSpeaking(false);
      synth.speak(utterance);
      setSpeaking(true);
    }
  };

  // Reading time + estimated audio duration, derived from word count
  const { readMinutes, audioLabel } = useMemo(() => {
    const words = blog?.content ? blog.content.trim().split(/\s+/).length : 0;
    const minutes = Math.max(1, Math.round(words / 200));
    const audioSeconds = Math.max(30, Math.round((words / 150) * 60));
    const mm = Math.floor(audioSeconds / 60);
    const ss = String(audioSeconds % 60).padStart(2, "0");
    return { readMinutes: minutes, audioLabel: `${mm}:${ss}` };
  }, [blog]);

  const handleLike = async () => {
    if (!userId) {
      navigate("/signin");
      return;
    }
    try {
      const res = await api.post(`/api/blogs/${id}/like`, { userId });
      setLiked(res.data.liked);
      setLikesCount((res.data.likes || []).length);
    } catch (err) {
      console.error("Failed to toggle like", err);
    }
  };

  const handleShare = async () => {
    try {
      const res = await api.post(`/api/blogs/${id}/share`);
      setSharesCount(res.data.shares);
      if (navigator.share) {
        await navigator.share({
          title: blog.title,
          text: blog.content.slice(0, 120),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copied to clipboard");
      }
    } catch (err) {
      console.error("Failed to share", err);
    }
  };

  const handleSave = async () => {
    if (!userId) {
      navigate("/signin");
      return;
    }
    try {
      const res = await api.post(`/api/blogs/${id}/save`, { userId });
      setSaved(res.data.saved);
      setSavesCount((res.data.saves || []).length);
    } catch (err) {
      console.error("Failed to toggle save", err);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    try {
      setSubmittingComment(true);
      const res = await api.post(`/api/blogs/${id}/comments`, {
        userId: userId || null,
        userName,
        text: newComment.trim(),
      });
      setComments((prev) => [...prev, res.data.comment]);
      setNewComment("");
      setShowCommentInput(false);
    } catch (err) {
      console.error("Failed to post comment", err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const scrollToComments = () => {
    setShowCommentInput(true);
    const el = document.getElementById("blog-comments");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading)
    return (
      <div className="text-center py-5 text-muted">
        <div className="spinner-border text-accent mb-3" role="status" />
        <Loader />
      </div>
    );

  if (!blog)
    return (
      <div className="text-center py-5 text-muted">
        Blog not found.
        <div className="mt-3">
          <button className="btn btn-accent" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(180deg, #f3f1fb 0%, #ffffff 45%)",
        paddingBottom: 110,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div className="container pt-4" style={{ maxWidth: 640 }}>
        {/* Top bar */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              border: "none",
              background: "#ffffff",
              boxShadow: "0 6px 16px rgba(17,15,45,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <ChevronLeft size={22} color="#1a1a2e" />
          </button>

          <button
            onClick={() => setSaved((s) => !s)}
            aria-label="Bookmark article"
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              border: "none",
              background: "#ffffff",
              boxShadow: "0 6px 16px rgba(17,15,45,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Bookmark
              size={19}
              color="#6d5dfc"
              fill={saved ? "#6d5dfc" : "none"}
            />
          </button>
        </div>

        {/* Category pill */}
        {blog.category && (
          <div
            className="d-inline-flex align-items-center gap-2 mb-3 px-3 py-2"
            style={{
              borderRadius: 999,
              background: "rgba(109,93,252,0.12)",
              color: "#6d5dfc",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.06em",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#6d5dfc",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#fff",
                }}
              />
            </span>
            {blog.category.toUpperCase()}
          </div>
        )}

        {/* Title */}
        <h1
          style={{
            fontSize: "2.1rem",
            fontWeight: 800,
            lineHeight: 1.18,
            letterSpacing: "-0.02em",
            color: "#14121f",
          }}
        >
          {blog.title}
        </h1>

        {/* Author row */}
        <div className="d-flex align-items-center gap-2 mt-3 mb-4">
          {blog.authorAvatar ? (
            <img
              src={blog.authorAvatar}
              alt={blog.author}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "#e7e3fb",
                color: "#6d5dfc",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
              }}
            >
              {blog.author ? blog.author.charAt(0).toUpperCase() : "?"}
            </div>
          )}
          <span style={{ fontSize: 14, color: "#5a5a6a" }}>
            by{" "}
            <strong style={{ color: "#6d5dfc" }}>{blog.author}</strong>
          </span>
          <span style={{ color: "#c3c1cf" }}>•</span>
          <span style={{ fontSize: 14, color: "#9a98a6" }}>
            {new Date(blog.createdAt).toLocaleDateString()}
          </span>
        </div>

        {/* Hero image */}
        {blog.mediaUrl && blog.mediaType === "image" && (
          <img
            src={blog.mediaUrl}
            alt={blog.title}
            className="w-100 mb-3"
            style={{
              borderRadius: 24,
              maxHeight: 340,
              objectFit: "cover",
            }}
          />
        )}

        {/* Meta pill row */}
        <div
          className="d-flex align-items-center justify-content-center gap-3 mb-3 py-2"
          style={{
            borderRadius: 999,
            background: "#ffffff",
            boxShadow: "0 6px 16px rgba(17,15,45,0.06)",
            fontSize: 14,
            color: "#4b4a58",
          }}
        >
          <span className="d-inline-flex align-items-center gap-2">
            <Clock size={16} color="#6d5dfc" />
            {readMinutes} min read
          </span>
          <span style={{ width: 1, height: 16, background: "#e6e4f0" }} />
          <span className="d-inline-flex align-items-center gap-2">
            <Headphones size={16} color="#6d5dfc" />
            Listen to article
          </span>
        </div>

        {/* Listen bar */}
        <button
          onClick={toggleSpeech}
          className="d-flex align-items-center justify-content-between w-100 mb-4 px-4 py-3"
          style={{
            borderRadius: 999,
            border: "none",
            background: "linear-gradient(135deg,#6d5dfc,#a18cff)",
            boxShadow: "0 12px 28px rgba(109,93,252,0.35)",
            cursor: "pointer",
          }}
        >
          <span className="d-inline-flex align-items-center gap-3">
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {speaking ? (
                <Pause size={15} color="#fff" fill="#fff" />
              ) : (
                <Play size={15} color="#fff" fill="#fff" />
              )}
            </span>
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>
              {speaking ? "Reading article…" : "Listen to this article"}
            </span>
          </span>
          <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
            {audioLabel}
          </span>
        </button>

        {/* Content */}
        <article
          style={{
            fontSize: 17,
            lineHeight: 1.9,
            color: "#2c2c2c",
            whiteSpace: "pre-line",
          }}
        >
          {blog.content}
        </article>

        {/* Related */}
        {relatedBlogs.length > 0 && (
          <section className="mt-5">
            <h4
              className="mb-3"
              style={{ fontWeight: 700, letterSpacing: "-0.01em" }}
            >
              More to read
            </h4>

            <div className="d-flex gap-3 overflow-auto pb-3">
              {relatedBlogs.map((b) => (
                <div
                  key={b._id}
                  onClick={() => navigate(`/blog/${b._id}`)}
                  style={{
                    minWidth: 240,
                    borderRadius: 18,
                    background: "#fff",
                    cursor: "pointer",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    transition: "transform .25s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-6px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  {b.mediaUrl && (
                    <img
                      src={b.mediaUrl}
                      alt={b.title}
                      style={{
                        width: "100%",
                        height: 140,
                        objectFit: "cover",
                        borderRadius: "18px 18px 0 0",
                      }}
                    />
                  )}
                  <div className="p-3">
                    <h6 style={{ fontWeight: 600, marginBottom: 6 }}>
                      {b.title}
                    </h6>
                    <small className="text-muted">
                      {new Date(b.createdAt).toLocaleDateString()}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Comments Section */}
        <section id="blog-comments" className="mt-5" style={{ marginBottom: 100 }}>
          <h4 style={{ fontWeight: 700, letterSpacing: "-0.01em", marginBottom: 16 }}>
            Comments ({comments.length})
          </h4>

          {showCommentInput && (
            <div className="mb-4 p-3" style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 14px rgba(0,0,0,0.05)" }}>
              <textarea
                className="w-100 border-0"
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
                style={{
                  resize: "vertical",
                  outline: "none",
                  fontSize: 15,
                  lineHeight: 1.6,
                  background: "transparent",
                }}
              />
              <div className="d-flex justify-content-end gap-2 mt-2">
                <button
                  onClick={() => {
                    setShowCommentInput(false);
                    setNewComment("");
                  }}
                  className="btn btn-sm"
                  style={{ color: "#6d5dfc" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePostComment}
                  disabled={submittingComment || !newComment.trim()}
                  className="btn btn-sm"
                  style={{
                    background: "var(--primary)",
                    color: "white",
                    borderRadius: 10,
                    padding: "6px 16px",
                    opacity: !newComment.trim() ? 0.5 : 1,
                  }}
                >
                  {submittingComment ? "Posting..." : "Post Comment"}
                </button>
              </div>
            </div>
          )}

          {comments.length === 0 ? (
            <p className="text-muted" style={{ fontSize: 14 }}>
              No comments yet. Be the first to share your thoughts!
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {comments.map((c) => (
                <div
                  key={c._id || `${c.userId}-${c.createdAt || Date.now()}`}
                  style={{
                    background: "#fff",
                    borderRadius: 14,
                    padding: 14,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: "#e7e3fb",
                        color: "#6d5dfc",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                      }}
                    >
                      {(c.userName || "A").charAt(0).toUpperCase()}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                      {c.userName || "Anonymous"}
                    </span>
                    <span style={{ fontSize: 11, color: "#999", marginLeft: "auto" }}>
                      {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <p style={{ fontSize: 14, lineHeight: 1.6, color: "#444", margin: 0 }}>
                    {c.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Bottom action bar */}
      <div
        className="d-flex align-items-center justify-content-around"
        style={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: 16,
          width: "calc(100% - 32px)",
          maxWidth: 608,
          borderRadius: 999,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(12px)",
          boxShadow: "0 16px 40px rgba(17,15,45,0.12)",
          padding: "12px 8px",
        }}
      >
        <button
          onClick={handleLike}
          className="d-inline-flex align-items-center gap-2 bg-transparent border-0"
          style={{ color: liked ? "#ff5a7a" : "#6d5dfc", fontWeight: 600, fontSize: 14 }}
        >
          <Heart size={18} fill={liked ? "#ff5a7a" : "none"} />
          {liked ? "Liked" : "Like"} ({likesCount})
        </button>
        <span style={{ width: 1, height: 20, background: "#e6e4f0" }} />
        <button
          onClick={handleShare}
          className="d-inline-flex align-items-center gap-2 bg-transparent border-0"
          style={{ color: "#6d5dfc", fontWeight: 600, fontSize: 14 }}
        >
          <Share2 size={18} />
          Share ({sharesCount})
        </button>
        <span style={{ width: 1, height: 20, background: "#e6e4f0" }} />
        <button
          onClick={handleSave}
          className="d-inline-flex align-items-center gap-2 bg-transparent border-0"
          style={{ color: saved ? "#6d5dfc" : "#6d5dfc", fontWeight: 600, fontSize: 14 }}
        >
          <Bookmark size={18} fill={saved ? "#6d5dfc" : "none"} />
          {saved ? "Saved" : "Save"} ({savesCount})
        </button>
        <span style={{ width: 1, height: 20, background: "#e6e4f0" }} />
        <button
          onClick={scrollToComments}
          className="d-inline-flex align-items-center gap-2 bg-transparent border-0"
          style={{ color: "#6d5dfc", fontWeight: 600, fontSize: 14 }}
        >
          <MessageCircle size={18} />
          Comment ({comments.length})
        </button>
      </div>
    </div>
  );
}
